'use client';

import React from 'react';
import { X, RotateCcw } from 'lucide-react';
import { categories, conditions, regions } from './feedData';
import type { Filters } from './ListingsFeedContent';

type Props = {
  filters: Filters;
  onChange: (f: Filters) => void;
  onReset: () => void;
  activeCount: number;
  onClose?: () => void;
};

export default function FilterPanel({ filters, onChange, onReset, activeCount, onClose }: Props) {
  function toggleArray(key: 'categories' | 'conditions' | 'regions', value: string) {
    const arr = filters[key];
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
    onChange({ ...filters, [key]: next });
  }

  return (
    <div className="sticky top-24">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white">Filters</span>
          {activeCount > 0 && (
            <span className="bg-amber-500 text-slate-900 text-xs font-bold px-1.5 py-0.5 rounded-full">{activeCount}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {activeCount > 0 && (
            <button onClick={onReset} className="flex items-center gap-1 text-xs text-slate-500 hover:text-amber-400 transition-colors duration-150 px-2 py-1 rounded-md hover:bg-amber-500/10">
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all duration-150">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {/* Category */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Category</p>
          <div className="flex flex-col gap-2">
            {categories.map((cat) => (
              <label key={cat.id} className="flex items-center justify-between cursor-pointer group">
                <div className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={filters.categories.includes(cat.slug)}
                    onChange={() => toggleArray('categories', cat.slug)}
                    className="filter-checkbox"
                  />
                  <span className={`text-sm transition-colors duration-150 ${filters.categories.includes(cat.slug) ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}>
                    {cat.label}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-[hsl(222,25%,18%)]" />

        {/* Condition */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Condition</p>
          <div className="flex flex-col gap-2">
            {conditions.map((cond) => (
              <label key={cond.id} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.conditions.includes(cond.value)}
                  onChange={() => toggleArray('conditions', cond.value)}
                  className="filter-checkbox"
                />
                <span className={`text-sm transition-colors duration-150 ${filters.conditions.includes(cond.value) ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}>
                  {cond.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-[hsl(222,25%,18%)]" />

        {/* Price range */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Price Range</p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
              <input
                type="number"
                value={filters.priceMin}
                onChange={(e) => onChange({ ...filters, priceMin: e.target.value })}
                placeholder="Min"
                min="0"
                className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-6 pr-2 py-2 text-sm focus:outline-none focus:border-amber-500/60 transition-all duration-150 tabular-nums"
              />
            </div>
            <span className="text-slate-600 text-sm">—</span>
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
              <input
                type="number"
                value={filters.priceMax}
                onChange={(e) => onChange({ ...filters, priceMax: e.target.value })}
                placeholder="Max"
                min="0"
                className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-6 pr-2 py-2 text-sm focus:outline-none focus:border-amber-500/60 transition-all duration-150 tabular-nums"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[hsl(222,25%,18%)]" />

        {/* Region */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Region</p>
          <div className="flex flex-col gap-2">
            {regions.map((region) => (
              <label key={region.id} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.regions.includes(region.value)}
                  onChange={() => toggleArray('regions', region.value)}
                  className="filter-checkbox"
                />
                <span className={`text-sm transition-colors duration-150 ${filters.regions.includes(region.value) ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}>
                  {region.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}